import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Radar, PlayCircle, RefreshCw, AlertTriangle, Plus, CheckSquare, Square,
  FileText, MessageSquare, Download, Eye, EyeOff
} from 'lucide-react';

const CasoInvestigar = ({
  caso, monitoresDisponibles, monitoresSeleccionados,
  investigando, resultadosInvestigacion, incluirWhitelist, agregandoAlerta,
  onToggleMonitor, onToggleTodos, onInvestigar,
  onSetIncluirWhitelist, onAgregarAlerta
}) => {
  // Recopilar notas de alertas como hallazgos
  const hallazgos = useMemo(() => {
    const items = [];
    (caso.alertas || []).forEach(alerta => {
      (alerta.notas || []).forEach(nota => {
        items.push({
          id: nota.id || `${alerta.id}-${nota.fecha}`,
          alerta_id: alerta.id,
          alerta_tipo: alerta.tipo || alerta.nombre_display || 'Alerta',
          alerta_severidad: alerta.severidad || 'MEDIA',
          tipo: nota.tipo,
          comentario: nota.comentario,
          usuario: nota.usuario,
          fecha: nota.fecha,
          incluir: true,
        });
      });
    });
    return items.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [caso]);

  const [excluidos, setExcluidos] = useState(new Set());

  const toggleExcluir = (id) => {
    setExcluidos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exportarHallazgos = () => {
    const incluidos = hallazgos.filter(h => !excluidos.has(h.id));
    if (incluidos.length === 0) return;
    
    let texto = `INFORME DE HALLAZGOS - ${caso.titulo || 'Caso'}\n`;
    texto += `Fecha de exportación: ${new Date().toLocaleString('es-AR')}\n`;
    texto += `Cliente: ${caso.nombre_padron || caso.cuit || 'N/A'}\n`;
    texto += `${'='.repeat(60)}\n\n`;
    
    incluidos.forEach((h, idx) => {
      texto += `${idx + 1}. [${h.alerta_tipo}] - ${h.tipo === 'ESCALAR' ? 'ESCALAMIENTO' : h.tipo === 'FALSO_POSITIVO' ? 'FALSO POSITIVO' : 'NOTA'}\n`;
      texto += `   Severidad: ${h.alerta_severidad}\n`;
      texto += `   Fecha: ${new Date(h.fecha).toLocaleString('es-AR')}\n`;
      texto += `   Autor: ${h.usuario}\n`;
      texto += `   Hallazgo: ${h.comentario}\n`;
      texto += `   Ref. Alerta: ${h.alerta_id}\n\n`;
    });
    
    const blob = new Blob([texto], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hallazgos_${caso.id?.slice(0, 8) || 'caso'}_${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Radar className="text-purple-600" /> Investigar con Monitores
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Ejecute monitores para buscar otras alertas potenciales relacionadas con este cliente
            {caso.cuit && <span className="font-medium"> (CUIT: {caso.cuit})</span>}
          </p>
        </div>
        <Button
          onClick={onInvestigar}
          disabled={investigando || monitoresSeleccionados.length === 0}
          className="bg-purple-600 hover:bg-purple-700 text-white"
        >
          {investigando ? (
            <><RefreshCw size={16} className="mr-2 animate-spin" /> Investigando...</>
          ) : (
            <><PlayCircle size={16} className="mr-2" /> Ejecutar ({monitoresSeleccionados.length})</>
          )}
        </Button>
      </div>

      <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <input
          type="checkbox" id="incluir-whitelist"
          checked={incluirWhitelist}
          onChange={(e) => onSetIncluirWhitelist(e.target.checked)}
          className="w-4 h-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
        />
        <label htmlFor="incluir-whitelist" className="flex-1 cursor-pointer">
          <span className="text-sm font-medium text-amber-800">Incluir cuentas en Whitelist</span>
          <p className="text-xs text-amber-600 mt-0.5">
            Por defecto se excluyen las cuentas de confianza (whitelist). Active esta opcion para incluirlas en la busqueda.
          </p>
        </label>
      </div>

      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-slate-700">Monitores Disponibles</h4>
          <button onClick={onToggleTodos} className="text-sm text-purple-600 hover:text-purple-800 font-medium">
            {monitoresSeleccionados.length === monitoresDisponibles.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
          {monitoresDisponibles.map(monitor => (
            <div
              key={monitor.id}
              onClick={() => onToggleMonitor(monitor.id)}
              className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                monitoresSeleccionados.includes(monitor.id)
                  ? 'bg-purple-100 border-purple-300 border'
                  : 'bg-white border border-slate-200 hover:border-purple-200'
              }`}
            >
              {monitoresSeleccionados.includes(monitor.id) ? (
                <CheckSquare size={18} className="text-purple-600 mt-0.5 flex-shrink-0" />
              ) : (
                <Square size={18} className="text-slate-400 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-slate-800 truncate">{monitor.nombre}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`text-xs ${
                    monitor.severidad === 'CRITICA' ? 'bg-red-100 text-red-700' :
                    monitor.severidad === 'ALTA' ? 'bg-orange-100 text-orange-700' :
                    monitor.severidad === 'MEDIA' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {monitor.severidad}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </div>
        {monitoresDisponibles.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">No hay monitores disponibles</p>
        )}
      </div>

      {resultadosInvestigacion.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <h4 className="font-semibold text-green-800 flex items-center gap-2">
              <AlertTriangle size={18} /> Coincidencias Encontradas ({resultadosInvestigacion.length})
            </h4>
            <p className="text-sm text-green-700 mt-1">Revise las coincidencias y agregue al caso las que considere relevantes</p>
          </div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {resultadosInvestigacion.map((resultado, idx) => (
              <div key={idx} className="p-4 hover:bg-slate-50">
                {resultado.error ? (
                  <div className="text-red-600 text-sm">Error en {resultado.monitor_nombre}: {resultado.error}</div>
                ) : (
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${
                          resultado.severidad === 'CRITICA' ? 'bg-red-100 text-red-700' :
                          resultado.severidad === 'ALTA' ? 'bg-orange-100 text-orange-700' :
                          resultado.severidad === 'MEDIA' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {resultado.severidad}
                        </Badge>
                        <span className="text-xs text-slate-500">{resultado.monitor_nombre}</span>
                      </div>
                      <p className="font-medium text-slate-800">{resultado.titulo}</p>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{resultado.descripcion}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        {resultado.monto_total && <span>Monto: ${resultado.monto_total?.toLocaleString()}</span>}
                        {resultado.cantidad_operaciones && <span>{resultado.cantidad_operaciones} operaciones</span>}
                        {resultado.cuenta_id && <span className="font-mono">Cuenta: {resultado.cuenta_id}</span>}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => onAgregarAlerta(resultado)}
                      disabled={agregandoAlerta === (resultado.monitor_id + resultado.cuenta_id)}
                      className="bg-green-600 hover:bg-green-700 text-white flex-shrink-0"
                    >
                      {agregandoAlerta === (resultado.monitor_id + resultado.cuenta_id) ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <><Plus size={14} className="mr-1" /> Agregar al Caso</>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!investigando && resultadosInvestigacion.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Radar size={48} className="mx-auto mb-4 opacity-50" />
          <p>Seleccione monitores y haga clic en "Ejecutar" para buscar alertas potenciales</p>
        </div>
      )}

      {/* Hallazgos y Evidencia de Alertas */}
      {hallazgos.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-sky-50 to-blue-50 flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-sky-800 flex items-center gap-2">
                <FileText size={18} /> Hallazgos y Evidencia ({hallazgos.length - excluidos.size} incluidos)
              </h4>
              <p className="text-sm text-sky-600 mt-1">
                Notas registradas en alertas del caso. Se incluyen por defecto en exportaciones.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={exportarHallazgos}
              disabled={hallazgos.length - excluidos.size === 0}
              className="bg-white text-sky-700 border-sky-300 hover:bg-sky-50"
            >
              <Download size={14} className="mr-1" /> Exportar
            </Button>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {hallazgos.map((h) => {
              const isExcluded = excluidos.has(h.id);
              return (
                <div key={h.id} className={`p-3 flex items-start gap-3 ${isExcluded ? 'opacity-40 bg-slate-50' : 'hover:bg-slate-50'}`}>
                  <button
                    onClick={() => toggleExcluir(h.id)}
                    className="mt-0.5 flex-shrink-0"
                    title={isExcluded ? 'Incluir en exportación' : 'Excluir de exportación'}
                  >
                    {isExcluded ? <EyeOff size={16} className="text-slate-400" /> : <Eye size={16} className="text-sky-500" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className={`text-[10px] ${
                        h.alerta_severidad === 'CRITICA' ? 'bg-red-100 text-red-700' :
                        h.alerta_severidad === 'ALTA' ? 'bg-orange-100 text-orange-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {h.alerta_severidad}
                      </Badge>
                      <span className="text-xs font-medium text-slate-700">{h.alerta_tipo}</span>
                      <Badge variant="outline" className={`text-[10px] ${
                        h.tipo === 'ESCALAR' ? 'border-orange-300 text-orange-600' :
                        h.tipo === 'FALSO_POSITIVO' ? 'border-green-300 text-green-600' :
                        'border-blue-300 text-blue-600'
                      }`}>
                        <MessageSquare size={10} className="mr-1" />
                        {h.tipo === 'ESCALAR' ? 'Escalamiento' : h.tipo === 'FALSO_POSITIVO' ? 'F. Positivo' : 'Nota'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-800">{h.comentario}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{h.usuario} - {new Date(h.fecha).toLocaleString('es-AR')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CasoInvestigar;
